/**
 * Convoy detail API — fetches single-offer details on demand from loads-ingest-service.
 *
 * The list/feed search returns a compact projection (city, state, weight, route distance,
 * payout); to populate the expanded "Load Detail" panel with street address, broker name,
 * accessorials, notes, and facility ratings we hit `OfferPage` via this endpoint.
 *
 * Auth: standard Numeo JWT — the backend resolves Convoy cookies from Firestore.
 * We don't cache here — `useConvoyOfferDetail` (or component-level caching) can layer on
 * top if needed; the typical user expands a load once or twice per session.
 */

import { loadsIngestServiceAxios } from 'setup/SetupAxios'

export interface ConvoyDetailAddress {
    full?: string | null
    streetNumber?: string | null
    street?: string | null
    city?: string | null
    state?: string | null
    postalCode?: string | null
}

export interface ConvoyDetailFacilityRating {
    averageScore?: number
    ratingsCount?: number
}

export interface ConvoyDetailStop {
    id: string
    type?: string | null
    loadingType?: string | null
    orderIndex?: number | null
    timezone?: string | null
    driverStartTime?: string | null
    driverEndTime?: string | null
    routeDistanceMiles?: number
    locationName?: string | null
    address?: ConvoyDetailAddress | null
    facilityRating?: ConvoyDetailFacilityRating | null
    coordinates?: { latitude?: number; longitude?: number } | null
}

export interface ConvoyDetailBroker {
    id?: string
    name?: string
    mcNumber?: string | null
    carrierPolicyLink?: string | null
    fastestCarrierPaymentSpeed?: string | null
    onboarding?: { onboardingMethod?: string | null; onboardingPacketUrl?: string | null } | null
}

export interface ConvoyDetailShipper {
    id?: string
    name?: string
}

export interface ConvoyDetailShipment {
    id: string
    routeDistanceMiles?: number
    accessorials?: string[]
    loadDescription?: string | null
    referenceId?: string | null
    isPowerOnly?: boolean
    isHazmat?: boolean
    fullTruckload?: boolean
    requestedTruckTypes?: string[]
    isPendingCarrierOnboarding?: boolean
    isBrokerCarrierAgreementRequired?: boolean
    dimensions?: { weight?: number; weightUnit?: string }
    shipper?: ConvoyDetailShipper | null
    broker?: ConvoyDetailBroker | null
    stops?: ConvoyDetailStop[]
}

export interface ConvoyOfferDetail {
    id: string
    convoyId?: string
    state?: string
    isFlexibleShipment?: boolean
    wasRequested?: boolean
    isDedicated?: boolean
    offeredPayout?: { id?: string; totalCents?: number }
    dispatchable?: { id?: string; bidOnly?: boolean; hasLatePenalty?: boolean; lastMatchingStrategy?: string }
    shipment?: ConvoyDetailShipment
}

/**
 * Fetch a single Convoy offer's full detail page.
 * @param offerId  the long S-prefixed offer id (route key, NOT the friendly convoyId)
 */
export async function fetchConvoyOfferDetail(offerId: string): Promise<ConvoyOfferDetail | null> {
    if (!offerId) return null
    const response = await loadsIngestServiceAxios.get<{ success: boolean; data: ConvoyOfferDetail | null }>(
        `/loads/loadboards/convoy/offer/${encodeURIComponent(offerId)}`
    )
    return response.data?.data ?? null
}
